/** Shared helpers for mouse-free form navigation (Tab/Enter moves to next field, submits on the last one). */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  'button:not([disabled]):not([tabindex="-1"])',
  "textarea:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"]):not([disabled])',
].join(", ");

export function getFocusableElements(container: ParentNode): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null
  );
}

/**
 * Moves focus to the next focusable element within `container` (defaults to the
 * closest <form>, falling back to document). Submits the form when already on
 * the last field.
 */
export function focusNextElement(current: HTMLElement, container?: HTMLElement | null): void {
  const scope = container ?? current.closest("form") ?? document.body;
  const focusable = getFocusableElements(scope);
  const index = focusable.indexOf(current);
  if (index === -1) return;

  const next = focusable[index + 1];
  if (next) {
    next.focus();
    return;
  }

  const form = current.closest("form");
  form?.requestSubmit();
}

/**
 * Generic <form onKeyDown> handler: pressing Enter on a field moves focus to the
 * next field instead of submitting immediately, and submits only from the last field.
 */
export function handleEnterAsTabNavigation(event: React.KeyboardEvent<HTMLFormElement>): void {
  if (event.key !== "Enter" || event.defaultPrevented) return;

  const target = event.target as HTMLElement;
  if (target.tagName === "TEXTAREA" || target.tagName === "BUTTON") return;

  event.preventDefault();
  focusNextElement(target, event.currentTarget);
}
